/**
 * ProgressBar — React (web) implementation.
 *
 * A rounded track div with a proportional fill div, exposed to assistive tech
 * via the progressbar role and aria-value attributes.
 */

/// <reference lib="dom" />

import React from 'react';
import {
  clampProgressValue,
  COMPACT_PROGRESS_BAR_TRACK_WIDTH,
  COMPACT_PROGRESS_BAR_HEIGHT,
  COMPACT_PROGRESS_BAR_INSET,
  CONTRAST_PROGRESS_BAR_FILL_COLOR,
  CONTRAST_PROGRESS_BAR_TRACK_COLOR,
  NEUTRAL_PROGRESS_BAR_TRACK_COLOR,
  NEUTRAL_PROGRESS_BAR_FILL_COLOR,
  NEUTRAL_PROGRESS_BAR_CONTAINER_COLOR,
  PROGRESS_BAR_FILL_COLOR,
  PROGRESS_BAR_HEIGHT,
  PROGRESS_BAR_RADIUS,
  PROGRESS_BAR_TRACK_COLOR,
  SLIM_PROGRESS_BAR_HEIGHT,
} from './ProgressBar';
import type { ProgressBarProps, WebProgressBarSize, WebProgressBarTone } from './ProgressBar';

export interface WebProgressBarProps extends Omit<ProgressBarProps, 'testID' | 'size' | 'tone'> {
  'data-testid'?: string;
  /** Adds the web-only `slim` track to the shared sizes. */
  size?: WebProgressBarSize;
  /** Adds the web-only `contrast` colours to the shared tones. */
  tone?: WebProgressBarTone;
}

function trackHeight(size: WebProgressBarSize): number {
  if (size === 'compact') return COMPACT_PROGRESS_BAR_HEIGHT;
  return size === 'slim' ? SLIM_PROGRESS_BAR_HEIGHT : PROGRESS_BAR_HEIGHT;
}

function trackColor(tone: WebProgressBarTone): string {
  if (tone === 'neutral') return NEUTRAL_PROGRESS_BAR_TRACK_COLOR;
  return tone === 'contrast' ? CONTRAST_PROGRESS_BAR_TRACK_COLOR : PROGRESS_BAR_TRACK_COLOR;
}

function fillColor(tone: WebProgressBarTone): string {
  if (tone === 'neutral') return NEUTRAL_PROGRESS_BAR_FILL_COLOR;
  return tone === 'contrast' ? CONTRAST_PROGRESS_BAR_FILL_COLOR : PROGRESS_BAR_FILL_COLOR;
}

export function ProgressBar({
  value,
  accessibilityLabel,
  'data-testid': testId,
  size = 'default',
  tone = 'success',
}: WebProgressBarProps) {
  const clamped = clampProgressValue(value);
  const percent = Math.round(clamped * 100);

  return (
    <div
      role="progressbar"
      aria-label={accessibilityLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      data-testid={testId}
      style={{
        width:
          size === 'compact'
            ? COMPACT_PROGRESS_BAR_TRACK_WIDTH + 2 * COMPACT_PROGRESS_BAR_INSET
            : '100%',
        padding: size === 'compact' ? COMPACT_PROGRESS_BAR_INSET : undefined,
        boxSizing: 'border-box',
        borderRadius: PROGRESS_BAR_RADIUS,
        backgroundColor: tone === 'neutral' ? NEUTRAL_PROGRESS_BAR_CONTAINER_COLOR : undefined,
      }}
    >
      <div
        style={{
          width: size === 'compact' ? COMPACT_PROGRESS_BAR_TRACK_WIDTH : '100%',
          height: trackHeight(size),
          borderRadius: PROGRESS_BAR_RADIUS,
          backgroundColor: trackColor(tone),
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: `${percent}%`,
            height: '100%',
            borderRadius: PROGRESS_BAR_RADIUS,
            backgroundColor: fillColor(tone),
          }}
        />
      </div>
    </div>
  );
}
